import { useState } from 'react';
import type { CheckpointType } from '../../../../shared/proposal-contract.js';
import { CHECKPOINT_LABELS, CHECKPOINT_ORDER } from '../../../../shared/checkpoint-labels.js';
import { Dialogue } from './Dialogue';
import { ChatBottomBar } from './ChatBottomBar';
import type { Message } from './MessageBubble';
import type { Proposal } from './ProposalBubble';
import { Badge } from '../ui';
// Типы обеих осей и подписи — из одного места: три копии слов «только в
// начатых беседах» разъехались бы, как разъезжается всё, объявленное дважды
import type { AutoReplyMode, GenerationMode } from '../../api/auto-reply';
import { AUTO_REPLY_MODE_INFO, GENERATION_MODE_INFO } from '../../lib/mode-labels';

interface RealPanelViewProps {
  messages: Message[];
  proposals: Proposal[];
  isLoading: boolean;
  error?: Error | null;
  disabled?: boolean;
  onSend: (text: string) => void;
  isPending?: boolean;
  sendError?: Error | null;
  /** Исход отправки, который не ошибка (например, «ответ уже отправлен»). */
  sendNotice?: string | null;
  onRetryMessage?: ((messageId: string) => void) | undefined;
  /** Phase 8+9: action callbacks */
  threadId?: string;
  onPromoteKnowledge?: (title: string, content: string) => void;
  onCheckpoint?: (type: CheckpointType) => void;
  onTakeoverChat?: () => void;
  onReturnToAI?: () => void;
  /**
   * Попросить черновик руками — кнопка «Подготовить ответ».
   *
   * Черновик по ней НЕ отправляется клиенту сам, даже если диалог стоит в
   * режиме бота: оператор просил показать текст.
   */
  onPrepareReply?: () => void;
  /** Идёт подготовка: кнопка обязана быть недоступна, иначе второе нажатие. */
  isPreparingReply?: boolean;
  /** Почему подготовка не удалась — текст для оператора, а не тишина. */
  prepareReplyError?: string | null;
  /** true = оператор забрал чат (shadow override активен) */
  isChatTakenOver?: boolean;
  /**
   * Что ДЕЙСТВУЕТ у этого диалога по оси доставки.
   *
   * До 10.09 этот проп не передавал ни один из трёх вызовов — бейдж режима был
   * мёртв, и оператор не видел, что в диалоге работает бот.
   */
  autoReplyMode?: AutoReplyMode | undefined;
  /** Что действует по оси генерации: браться ли за черновик. */
  generationMode?: GenerationMode | undefined;
  /** У диалога СВОЙ режим хотя бы по одной оси — для бейджа. */
  hasOwnModes?: boolean;
  /**
   * Своё значение именно у оси ГЕНЕРАЦИИ.
   *
   * Отдельно от `hasOwnModes`, и это не дробление ради красоты: у диалога,
   * который забрали (переопределена доставка), общий признак истинен, а
   * генерация наследуется — и переключатель показал бы УНАСЛЕДОВАННЫЙ режим
   * как выбранный для диалога. Снять его было бы нечем: «как в настройках»
   * уже и так действует.
   */
  generationIsOwn?: boolean;
  /** Режим генерации ДЛЯ ЭТОГО диалога; `null` — снять и наследовать снова. */
  onGenerationModeChange?: (mode: GenerationMode | null) => void;
  /**
   * Почему смена режима диалога не удалась — словами.
   *
   * Ось доставки оператору недоступна («Забрать» ответит 403), и без этой
   * строки нажатие просто не приводило ни к чему: тихий отказ.
   */
  modeError?: string | null;
  onCorrectMessage?: (messageId: string, correctedText: string) => void;
}

/**
 * Real Panel — реальный диалог с возможностью отправки.
 *
 * Показывает:
 * - Header с иконкой и названием
 * - Dialogue (все сообщения + shadow matches)
 * - ChatBottomBar в режиме input
 */
export function RealPanelView({
  messages,
  proposals,
  isLoading,
  error,
  disabled = false,
  onSend,
  isPending = false,
  sendError,
  sendNotice = null,
  onRetryMessage,
  threadId,
  onPromoteKnowledge,
  onCheckpoint,
  onTakeoverChat,
  onReturnToAI,
  onPrepareReply,
  isPreparingReply = false,
  prepareReplyError = null,
  isChatTakenOver = false,
  autoReplyMode,
  generationMode,
  hasOwnModes = false,
  generationIsOwn = false,
  onGenerationModeChange,
  modeError = null,
  onCorrectMessage,
}: RealPanelViewProps): JSX.Element {
  const [showPromote, setShowPromote] = useState(false);
  const [showCheckpoint, setShowCheckpoint] = useState(false);
  const [promoteTitle, setPromoteTitle] = useState('');
  const [promoteContent, setPromoteContent] = useState('');

  // Последний outbound message (для promote)
  const outbounds = messages.filter(m => m.isOutbound);
  const lastOutbound = outbounds.length > 0 ? outbounds[outbounds.length - 1] : undefined;

  return (
    <div className="flex-1 min-w-0 flex flex-col">
      {/* Header */}
      <div className="px-6 py-3 border-b border-border-subtle bg-accent-brand/5">
        {/*
          Шапка ОБЯЗАНА переноситься. Замерено 10.09 в браузере: при ширине
          окна 1280 (13-дюймовый мак) селект режима начинался на 1273-й точке,
          «Забрать» — на 1498-й, а документ шире окна НЕ был, то есть
          прокруткой их было не достать. Кнопки существовали в разметке и были
          недоступны — тот же класс, что мёртвый бейдж: элемент есть, человеку
          его нет.
        */}
        <div className="flex items-center justify-between flex-wrap gap-y-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 rounded-full bg-accent-brand/20 flex items-center justify-center">
              <svg className="w-3 h-3 text-accent-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <span className="text-sm font-medium text-accent-brand">Real</span>
            <span className="text-xs text-text-secondary">реальный диалог</span>
            {autoReplyMode && autoReplyMode !== 'shadow' && (
              <Badge variant="ai">{AUTO_REPLY_MODE_INFO[autoReplyMode].label}</Badge>
            )}
            {/*
              Признак «у диалога свой режим» обязателен: без него оператор
              переключил настройку диалога и не видит, что она действует, —
              это ровно тот сигнал провала, который записан в спеке шага 5.
            */}
            {hasOwnModes && <Badge variant="waiting">свой режим</Badge>}
          </div>

          {/* Action buttons — compact icons */}
          {!disabled && threadId && (
            <div className="flex items-center gap-2 flex-wrap justify-end min-w-0">
              {onPromoteKnowledge && (
                <button
                  onClick={() => { setPromoteTitle(''); setPromoteContent(lastOutbound?.text ?? ''); setShowPromote(!showPromote); }}
                  className="p-1.5 rounded-comp text-text-muted hover:text-accent-brand hover:bg-accent-brand/10 transition-colors"
                  title="Сохранить как знание"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" /></svg>
                </button>
              )}

              {onCheckpoint && (
                <div className="relative">
                  <button
                    onClick={() => setShowCheckpoint(!showCheckpoint)}
                    className="p-1.5 rounded-comp text-text-muted hover:text-accent-success hover:bg-accent-success/10 transition-colors"
                    title="Отметить результат"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" /></svg>
                  </button>
                  {showCheckpoint && (
                    <div className="absolute right-0 top-full mt-1 bg-surface-elevated border border-border-default rounded-comp shadow-lg z-10 py-1 w-44">
                      {CHECKPOINT_ORDER.map(type => (
                        <button
                          key={type}
                          onClick={() => { onCheckpoint(type); setShowCheckpoint(false); }}
                          className="w-full text-left px-3 py-1.5 text-sm text-text-primary hover:bg-surface-subtle transition-colors"
                        >
                          {CHECKPOINT_LABELS[type]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {onPrepareReply && (
                <button
                  onClick={onPrepareReply}
                  disabled={isPreparingReply}
                  className="px-2.5 py-1 text-xs rounded-comp bg-accent-brand/10 text-accent-brand border border-accent-brand/20 hover:bg-accent-brand/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium whitespace-nowrap"
                  title="Движок подготовит черновик ответа. Отправку решаете вы"
                >
                  {isPreparingReply ? 'Готовлю…' : 'Подготовить ответ'}
                </button>
              )}

              {onGenerationModeChange && (
                /*
                 * Ось генерации ДЛЯ ЭТОГО диалога. Родной `select`, а не своё
                 * выпадающее меню: четыре значения, одно из них — «как в
                 * настройках», и снятие переопределения обязано быть таким же
                 * доступным, как установка.
                 */
                <select
                  value={generationIsOwn && generationMode ? generationMode : ''}
                  onChange={(e) =>
                    onGenerationModeChange(
                      e.target.value === '' ? null : (e.target.value as GenerationMode)
                    )
                  }
                  title="Когда готовить черновик в этом диалоге"
                  className={[
                    'px-2 py-1 text-xs rounded-comp bg-surface-base text-text-secondary',
                    // max-w-full, а не фиксированные 13rem: панель диалога на
                    // узком окне уже 182 точек, и селект в 208 обрезался
                    // вместе с соседними кнопками (замер 10.09 при окне 900)
                    'border transition-colors cursor-pointer max-w-full min-w-0',
                    generationIsOwn
                      ? 'border-accent-brand text-accent-brand'
                      : 'border-border-default',
                  ].join(' ')}
                >
                  <option value="">Черновики: как в настройках</option>
                  {(Object.keys(GENERATION_MODE_INFO) as GenerationMode[]).map((mode) => (
                    <option key={mode} value={mode}>
                      Черновики: {GENERATION_MODE_INFO[mode].label.toLowerCase()}
                    </option>
                  ))}
                </select>
              )}

              <div className="w-px h-4 bg-border-subtle" />

              {(onTakeoverChat || onReturnToAI) && (
                isChatTakenOver ? (
                  <button
                    onClick={onReturnToAI}
                    className="px-2.5 py-1 text-xs rounded-comp bg-accent-success/10 text-accent-success border border-accent-success/20 hover:bg-accent-success/20 transition-colors font-medium whitespace-nowrap"
                    title="Вернуть AI-ответы"
                  >
                    Вернуть AI
                  </button>
                ) : (
                  <button
                    onClick={onTakeoverChat}
                    className="px-2.5 py-1 text-xs rounded-comp bg-accent-danger/10 text-accent-danger border border-accent-danger/20 hover:bg-accent-danger/20 transition-colors font-medium whitespace-nowrap"
                    title="Переключить в shadow"
                  >
                    Забрать
                  </button>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* Promote modal */}
      {showPromote && onPromoteKnowledge && (
        <div className="px-4 py-3 border-b border-border-subtle bg-surface-subtle">
          <div className="text-xs font-medium text-text-primary mb-2">Сохранить как знание</div>
          <input
            type="text"
            value={promoteTitle}
            onChange={(e) => setPromoteTitle(e.target.value)}
            placeholder="Вопрос клиента (заголовок)"
            className="w-full px-2 py-1 mb-2 text-sm bg-surface-base border border-border-default rounded-comp text-text-primary"
          />
          <textarea
            value={promoteContent}
            onChange={(e) => setPromoteContent(e.target.value)}
            rows={2}
            className="w-full px-2 py-1 mb-2 text-sm bg-surface-base border border-border-default rounded-comp text-text-primary resize-none"
          />
          <div className="flex gap-2">
            <button
              onClick={() => { onPromoteKnowledge(promoteTitle, promoteContent); setShowPromote(false); }}
              disabled={!promoteTitle.trim() || !promoteContent.trim()}
              className="px-3 py-1 text-xs bg-accent-brand text-white rounded-comp disabled:opacity-50"
            >
              Сохранить
            </button>
            <button
              onClick={() => setShowPromote(false)}
              className="px-3 py-1 text-xs text-text-secondary hover:bg-surface-subtle rounded-comp"
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {disabled ? (
          <div className="flex flex-col items-center justify-center h-full text-text-secondary p-4">
            <p className="text-sm">Выберите чат</p>
          </div>
        ) : (
          <Dialogue
            messages={messages}
            proposals={proposals}
            isLoading={isLoading}
            error={error ?? null}
            onRetryMessage={onRetryMessage}
            {...(onCorrectMessage ? { onCorrectMessage } : {})}
          />
        )}
      </div>

      {/*
       * Почему черновик не готовится. Без этой строки отказ был бы тихим:
       * оператор жмёт «Подготовить ответ», получает 409 или 503, и на экране
       * не меняется ничего. Ровно этот дефект уже находили в принятии
       * черновиков — там у мутации не было обработчика ошибки
       */}
      {prepareReplyError && (
        <div className="px-6 py-2 text-xs text-accent-danger bg-accent-danger/10 border-t border-accent-danger/20">
          {prepareReplyError}
        </div>
      )}

      {modeError && (
        <div className="px-6 py-2 text-xs text-accent-danger bg-accent-danger/10 border-t border-accent-danger/20">
          Режим диалога не изменён: {modeError}
        </div>
      )}

      {/* Bottom Bar */}
      <ChatBottomBar
        mode="input"
        onSend={onSend}
        isPending={isPending}
        sendError={sendError ?? null}
        sendNotice={sendNotice}
        disabled={disabled}
      />
    </div>
  );
}
